/**
 * Component for managing push notification subscription
 */

import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
    BellAlertIcon,
    BellSlashIcon,
    QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { PushNotificationHelpModal } from "./PushNotificationHelpModal";

interface PushNotificationButtonProps {
    variant?: "village" | "longhouse";
}

export function PushNotificationButton({
    variant = "village",
}: PushNotificationButtonProps) {
    const {
        permission,
        isSubscribed,
        isLoading,
        isSupported,
        subscribe,
        unsubscribe,
    } = usePushNotifications();
    const [showHelp, setShowHelp] = useState(false);

    if (!isSupported) {
        return null; // Don't show button if not supported
    }

    const handleToggle = async () => {
        console.debug("Button clicked! isSubscribed:", isSubscribed);
        try {
            if (isSubscribed) {
                console.debug("Attempting to unsubscribe...");
                await unsubscribe();
                console.debug("Unsubscribe completed");
            } else {
                console.debug("Attempting to subscribe...");
                await subscribe();
                console.debug("Subscribe completed");
            }
        } catch (error) {
            console.error("Failed to toggle push notifications:", error);
            // Only show error for subscribe failures
            if (!isSubscribed) {
                alert(
                    `Error: ${error instanceof Error ? error.message : String(error)}`,
                );
            }
        }
    };

    const getButtonClasses = () => {
        const baseClasses =
            "flex items-center gap-2 rounded px-3 py-2 text-sm font-medium";

        if (variant === "village") {
            return `${baseClasses} ${
                isSubscribed
                    ? "bg-brand/20 text-brand hover:bg-brand/30"
                    : "bg-slate-700 text-slate-300 hover:bg-slate-600"
            }`;
        } else {
            return `${baseClasses} ${
                isSubscribed
                    ? "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/40"
                    : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            }`;
        }
    };

    const getIcon = () => {
        const iconClass = "h-5 w-5";
        return isSubscribed ? (
            <BellAlertIcon className={iconClass} />
        ) : (
            <BellSlashIcon className={iconClass} />
        );
    };

    const getLabel = () => {
        if (permission === "denied") {
            return "Browser notifications blocked";
        }
        return isSubscribed
            ? "Push notifications enabled"
            : "Enable push notifications";
    };

    return (
        <>
            <div className="flex items-center gap-2">
                <button
                    onClick={handleToggle}
                    disabled={isLoading || permission === "denied"}
                    className={getButtonClasses()}
                    title={getLabel()}
                >
                    {getIcon()}
                    <span>{isLoading ? "Loading..." : getLabel()}</span>
                </button>
                <button
                    onClick={() => setShowHelp(true)}
                    className={`rounded p-2 ${
                        variant === "village"
                            ? "hover:text-brand hover:bg-brand/10 text-slate-400"
                            : "text-slate-500 hover:bg-blue-50 hover:text-blue-600 dark:text-slate-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                    }`}
                    title="Push notifications help"
                    aria-label="Push notifications help"
                >
                    <QuestionMarkCircleIcon className="h-5 w-5" />
                </button>
            </div>
            <PushNotificationHelpModal
                isOpen={showHelp}
                onClose={() => setShowHelp(false)}
                variant={variant}
            />
        </>
    );
}
